import React, { useRef, useState } from 'react';
import styled from "styled-components";
import { Input } from "ui-kit/PrimitiveControls/Input";
import { Select } from "ui-kit/PrimitiveControls/Select";
import Edit from "assets/edit.svg";
import { Button } from "ui-kit/Button";
import { Toast } from "primereact/toast";
import { Country } from "types/country";
import {FormattedMessage} from "react-intl";
import IntlStore from "stories/IntlStore";

interface OverviewProps {
  country: Country;
  genres: string;
  year: string;
  artists: string;
  updating: boolean;
  countries: Country[];
  onSave: (data: any) => Promise<void>;
}

export const Overview: React.FC<OverviewProps> = props => {
  const { country, updating, genres, onSave, artists, year, countries } = props;
  const [inputYear, setInputYear] = useState(year);
  const [isEditingYear, setEditingYear] = useState(false);
  const toast = useRef<any>(null);

  const onSaveYear = async () => {
    try {
      await onSave({ year: inputYear });
      setEditingYear(false);
      toast.current?.show({
        severity: 'success',
        summary: IntlStore.intl.formatMessage({
          id: "Labels.Success",
          defaultMessage: "Success"
        }),
        detail: IntlStore.intl.formatMessage({
          id: "Labels.NewYearSavedSuccessfully",
          defaultMessage: "New year saved successfully"
        }),
      });
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: IntlStore.intl.formatMessage({
          id: "Labels.Error",
          defaultMessage: "Error"
        }),
        detail: e.message
      });
    }
  }

  const onSaveCountry = async (value: string) => {
    try {
      await onSave({ country_id: value });
      setEditingYear(false);
      toast.current?.show({
        severity: 'success',
        summary: IntlStore.intl.formatMessage({
          id: "Labels.Success",
          defaultMessage: "Success"
        }),
        detail: IntlStore.intl.formatMessage({
          id: "Labels.NewCountrySavedSuccessfully",
          defaultMessage: "New country saved successfully"
        }),
      });
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: IntlStore.intl.formatMessage({
          id: "Labels.Error",
          defaultMessage: "Error"
        }),
        detail: e.message
      });
    }
  }

  return (
    <Container>
      <Title>
        <FormattedMessage
          id="Labels.Overview.Title"
          defaultMessage="Overview"
        />
      </Title>
      <Content>
        <Item>
          <ItemTitle>
            <FormattedMessage
              id="Labels.Overview.LabelCountry"
              defaultMessage="Label country"
            />
          </ItemTitle>
          <ItemValue>
            <Select
              name="country"
              filter
              value={country.id}
              items={countries.map(country => ({ label: country.name, value: country.id, iso: country.iso }))}
              onChange={(e) => {
                const value = e.target.value;
                onSaveCountry(value);
              }}
              valueTemplate={selectedCountryTemplate}
              itemTemplate={countryOptionTemplate}
            />
          </ItemValue>
        </Item>
        <Item>
          <ItemTitle>
            <FormattedMessage
              id="Labels.Overview.Founded"
              defaultMessage="Founded"
            />
          </ItemTitle>
          <ItemValue>
            {isEditingYear ? (
              <EditYearContainer>
                <Input name="description" value={inputYear} onChange={(e) => setInputYear(e.target.value)} />
                <SaveButton onClick={onSaveYear} loading={updating} disabled={inputYear.length === 0 || inputYear === year}>
                  <FormattedMessage
                    id="Labels.Overview.Save"
                    defaultMessage="Save"
                  />
                </SaveButton>
              </EditYearContainer>
            ) : (
              <InfoYear>
                {year || '-'}
                <EditButton onClick={() => setEditingYear(true)}>
                  <Edit />
                </EditButton>
              </InfoYear>
            )}
          </ItemValue>
        </Item>
        <Item>
          <ItemTitle>
            <FormattedMessage
              id="Labels.Overview.Genres"
              defaultMessage="Genres"
            />
          </ItemTitle>
          <ItemValue>{genres}</ItemValue>
        </Item>
        <Item>
          <ItemTitle>
            <FormattedMessage
              id="Labels.Overview.MainArtists"
              defaultMessage="Main artists"
            />
          </ItemTitle>
          <ItemTitle>{artists}</ItemTitle>
        </Item>
      </Content>
      <Toast ref={toast} />
    </Container>
  );
};

const selectedCountryTemplate = (option: any) => {
  if (option) {
    return (
      <CountrySelectedItem>
        <CountryName>{option.label}</CountryName>
        <CountryCode>[{option.iso}]</CountryCode>
      </CountrySelectedItem>
    );
  }

  return (
    <CountrySelectedItem>
      <CountryName>-</CountryName>
    </CountrySelectedItem>
  );
};

const countryOptionTemplate = (option: any) => {
  return (
    <CountryOptionItem>
      <CountryName>{option.label}</CountryName>
      <CountryCode>{option.iso}</CountryCode>
    </CountryOptionItem>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  border-radius: 30px;
  background-color: #ffffff;
  padding: 20px 20px 30px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  line-height: 25px;
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 10px;
  
  .p-dropdown-item-label {
    width: 100% !important;
  }
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const ItemTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  color: rgba(0, 0, 0, 0.5);
`;

const ItemValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  color: #000000;
`;

const EditYearContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  max-width: 60%;
`;

const SaveButton = styled(Button)`
  min-width: 60px;
`;

const InfoYear = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  border: 1px solid #A4A4A4;
  border-radius: 8px;
  padding: 8px 10px;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
`;

const EditButton = styled.div`
  cursor: pointer;
`;

const CountryOptionItem = styled.div`
  display: flex;
  justify-content: space-between;
  width: 250px;
`;

const CountrySelectedItem = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
`;

const CountryCode = styled.div`
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 0, 0, 0.5);
`;

const CountryName = styled.div`
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: #000000;
`;
